import { Loader2, Shield, UserPlus, X } from "lucide-react";
import { Button, Input, Label } from "@cieda/djayma-ui";

import { ADMIN_THEME } from "@/lib/admin-theme";

import { DEPARTMENTS } from "./constants";
import type { InviteFormState } from "./types";

// ---------------------------------------------------------------------------
// Invite admin modal
// ---------------------------------------------------------------------------

interface InviteAdminModalProps {
  form: InviteFormState;
  onClose: () => void;
  onSubmit: () => void;
  onUpdateField: <K extends keyof InviteFormState>(
    field: K,
    value: InviteFormState[K],
  ) => void;
  isSubmitting: boolean;
}

export function InviteAdminModal({
  form,
  onClose,
  onSubmit,
  onUpdateField,
  isSubmitting,
}: InviteAdminModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Modal card */}
      <div
        className="relative z-10 w-full max-w-md rounded-xl border bg-white p-6 shadow-xl"
        style={{ borderColor: "#e7e0d6" }}
      >
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" style={{ color: ADMIN_THEME.accent.green }} />
            <h3 className="text-base font-semibold text-stone-800">
              Inviter un administrateur
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="inviteFullName" className="text-sm text-stone-700">
              Nom complet
            </Label>
            <Input
              id="inviteFullName"
              placeholder="Mamadou Ndiaye"
              value={form.full_name}
              onChange={(e) => onUpdateField("full_name", e.target.value)}
              className="border-stone-200 bg-stone-50 focus:border-stone-300"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="inviteEmail" className="text-sm text-stone-700">
              Adresse email
            </Label>
            <Input
              id="inviteEmail"
              type="email"
              placeholder="nom@cieda.sn"
              value={form.email}
              onChange={(e) => onUpdateField("email", e.target.value)}
              className="border-stone-200 bg-stone-50 focus:border-stone-300"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="inviteRole" className="text-sm text-stone-700">
              Rôle
            </Label>
            <select
              id="inviteRole"
              value={form.role}
              onChange={(e) => onUpdateField("role", e.target.value as "manager" | "agent")}
              className="flex h-9 w-full rounded-md border border-stone-200 bg-stone-50 px-3 py-1 text-sm shadow-sm transition-colors focus:border-stone-300 focus:outline-none focus:ring-1 focus:ring-stone-300"
            >
              <option value="manager">Manager</option>
              <option value="agent">Agent</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="inviteDept" className="text-sm text-stone-700">
              Département
            </Label>
            <select
              id="inviteDept"
              value={form.department_id}
              onChange={(e) => onUpdateField("department_id", e.target.value)}
              className="flex h-9 w-full rounded-md border border-stone-200 bg-stone-50 px-3 py-1 text-sm shadow-sm transition-colors focus:border-stone-300 focus:outline-none focus:ring-1 focus:ring-stone-300"
            >
              <option value="">Sélectionner un département</option>
              {DEPARTMENTS.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invitePlatform" className="text-sm text-stone-700">
              Plateforme
            </Label>
            <select
              id="invitePlatform"
              value={form.platform}
              onChange={(e) => onUpdateField("platform", e.target.value as "cieda" | "djayma")}
              className="flex h-9 w-full rounded-md border border-stone-200 bg-stone-50 px-3 py-1 text-sm shadow-sm transition-colors focus:border-stone-300 focus:outline-none focus:ring-1 focus:ring-stone-300"
            >
              <option value="djayma">Djayma</option>
              <option value="cieda">CIEDA</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-stone-200 text-stone-600"
            >
              Annuler
            </Button>
            <Button
              onClick={onSubmit}
              disabled={isSubmitting}
              className="gap-2 text-white"
              style={{ background: ADMIN_THEME.accent.green }}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              {isSubmitting ? "Création..." : "Créer l'administrateur"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
